import { useForm } from 'react-hook-form';
import { Navigate, useLocation } from 'react-router';
import type { Platform } from '@dintiko/shared';
import { Button } from '../components/button.js';
import { Icon } from '../components/icon.js';
import { PanelField } from '../components/panel-field.js';
import { Input } from '../components/field.js';
import { PlatformLogo } from '../components/platform-logo.js';
import { useLogin, useSession } from '../data/session.js';
import { platformName } from '../lib/status-labels.js';
import { ApiError } from '../lib/api.js';

interface LoginForm {
  email: string;
  password: string;
}

/** The five the app publishes to, in the order the composer lists them. */
const DESTINATIONS: Platform[] = ['youtube', 'instagram', 'tiktok', 'facebook', 'linkedin'];

/**
 * The only way in (PLAN §15: one owner account, created from the CLI, no
 * sign-up route).
 *
 * Two halves. The left is a slab of ink that says what the app is — it is the
 * first screen anyone sees, including a platform reviewer watching the audit
 * recording, and "upload once, publish everywhere" answers the question they
 * are about to ask. It stays black in both themes on purpose (--aw-panel),
 * because it is the product's face rather than a surface that follows a
 * setting.
 *
 * The right is the form and nothing else: no card, no box, two ruled lines and
 * a black button. There is one owner and two fields, so chrome around them
 * would be ceremony.
 *
 * The form deliberately says nothing about which half was wrong — the API
 * keeps missing-email and wrong-password in the same class so the endpoint
 * cannot be used to discover whether an address exists.
 */
export function LoginScreen() {
  const session = useSession();
  const login = useLogin();
  const location = useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({ defaultValues: { email: '', password: '' } });

  if (session.data) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/'} replace />;
  }

  const failure = login.error instanceof ApiError ? login.error : null;

  return (
    <main className="grid min-h-dvh grid-rows-[auto_1fr] bg-ground lg:grid-cols-2 lg:grid-rows-none">
      {/* ── left: the slab ──
          On a phone this is a short band above the form rather than half the
          screen — the owner signs in here most often, and a full-height pitch
          would push the fields below the fold on every visit. */}
      <section className="aw-panel-glow flex flex-col justify-between gap-8 bg-panel px-6 py-7 text-on-panel lg:px-12 lg:py-12">
        <PanelField />

        <div className="aw-enter aw-enter-1 flex items-center gap-2.5">
          <img
            src="/icons/icon-192.png"
            alt=""
            width={32}
            height={32}
            className="shrink-0 rounded-[22%]"
          />
          <span className="font-display text-xl leading-none tracking-[-0.012em]">dintiko</span>
        </div>

        <p className="aw-enter aw-enter-2 max-w-[16ch] font-display text-[clamp(26px,5vw,44px)] leading-[1.1] tracking-[-0.02em] text-balance">
          One upload. Every account.
        </p>

        <ul className="aw-enter aw-enter-3 flex flex-wrap items-center gap-x-4 gap-y-2.5 lg:flex-col lg:items-start lg:gap-2.5">
          {DESTINATIONS.map((platform) => (
            <li
              key={platform}
              className="flex items-center gap-2 text-sm text-panel-muted lg:text-md"
            >
              <PlatformLogo platform={platform} size={18} onPanel />
              {/* Each platform's own casing — `capitalize` on the id produced
                  "Youtube", "Tiktok" and "Linkedin", which is three brand
                  names spelled wrong on the first screen anyone sees. */}
              <span>{platformName(platform)}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* ── right: the form ──
          aw-paper keeps this half light in the dark theme too. Without it both
          halves resolved to #000 and the split disappeared entirely.

          text-ink is not redundant: `color` is inherited from body as an
          already-computed white, so redefining --aw-ink here does not reach
          anything that has no colour class of its own. */}
      <section className="aw-paper flex items-center justify-center bg-ground px-6 py-10 text-ink lg:px-12">
        <div className="flex w-full max-w-[21rem] flex-col gap-7">
          <div className="aw-enter aw-enter-2">
            <h1 className="font-display text-2xl leading-tight tracking-[-0.015em] text-ink">
              Sign in
            </h1>
            <p className="mt-1 text-sm text-muted">
              Upload once, caption once, publish everywhere.
            </p>
          </div>

          <form
            noValidate
            className="aw-enter aw-enter-4 flex flex-col gap-5"
            onSubmit={(event) => {
              void handleSubmit((values) => {
                login.mutate(values);
              })(event);
            }}
          >
            <Input
              label="Email"
              variant="underline"
              leading={<Icon name="mail" size={17} />}
              type="email"
              autoComplete="username"
              inputMode="email"
              aria-required="true"
              {...(errors.email?.message ? { error: errors.email.message } : {})}
              {...register('email', { required: 'Enter your email address.' })}
            />

            <Input
              label="Password"
              variant="underline"
              leading={<Icon name="lock" size={17} />}
              type="password"
              autoComplete="current-password"
              aria-required="true"
              {...(errors.password?.message ? { error: errors.password.message } : {})}
              {...register('password', { required: 'Enter your password.' })}
            />

            {failure && (
              <p role="alert" className="flex gap-1.5 text-sm text-bad">
                <span aria-hidden="true">✕</span>
                <span>{failure.message}</span>
              </p>
            )}

            <Button type="submit" variant="primary" busy={login.isPending} className="mt-1 w-full">
              Sign in
            </Button>
          </form>

          <p className="aw-enter aw-enter-5 text-xs text-faint">
            <a className="underline underline-offset-2" href="/privacy">
              Privacy
            </a>
            {' · '}
            <a className="underline underline-offset-2" href="/terms">
              Terms
            </a>
          </p>
        </div>
      </section>
    </main>
  );
}
